import { Button, Input, ListBoxItem, Select } from '@atlas/ui-primitives'
import { useNavigate } from 'react-router-dom'
import { ArticleFilterRail, ArticleFilterRailSkeleton } from '@/components/articles/ArticleFilterRail'
import { PageHeader } from '@/components/editorial/PageHeader'
import { useArticleFilterRailAxes } from '@/hooks/useArticleFilterRail'
import { useArticles } from '@/hooks/useArticles'
import { apiErrorMessage } from '@/lib/api-error'
import { ArticleErrorState, EmptyCorpusState, FilteredEmptyState } from './ArticleIndexStates'
import { ArticleResultList, ArticleResultListSkeleton } from './ArticleResultList'
import { ARTICLE_SORTS, type ArticleSort, useArticleIndexFilters } from './useArticleIndexFilters'

/** How many articles are on screen, stated so the single-page limit is visible. */
function countLabel(count: number): string {
	return `${count} article${count === 1 ? '' : 's'}`
}

/**
 * The articles index (route `/articles`) — a filter rail beside a results list.
 *
 * The two empty states are drawn apart from THIS page's filter state, not from a
 * second request: the API answers "nothing exists" and "your filters exclude
 * everything" with the same 204, and asking again unfiltered would double the
 * index's requests to learn something it already knows.
 */
export function ArticlesPage() {
	const navigate = useNavigate()
	const filters = useArticleIndexFilters()
	const { axes, isLoading: axesLoading } = useArticleFilterRailAxes(filters.shownAxisCodes, filters.params)
	const { data: articles = [], isLoading, isError, error, refetch } = useArticles(filters.params)

	const openComposer = (articleId: string) => void navigate(`/articles/${articleId}/edit`)

	return (
		<div>
			<PageHeader
				eyebrow="Content"
				title="Articles"
				subtitle={isLoading ? 'Long-form editorial content.' : countLabel(articles.length)}
				actions={
					<Button className="rounded-ds-full px-5" onPress={() => void navigate('/articles/new')}>
						New article
					</Button>
				}
			/>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
				{axesLoading ? (
					<ArticleFilterRailSkeleton label="Loading the filters…" />
				) : (
					<ArticleFilterRail
						typeSelection={filters.typeSelection}
						onToggleType={filters.toggleType}
						statusSelection={filters.statusSelection}
						onToggleStatus={filters.toggleStatus}
						axes={axes}
						shownAxisCodes={filters.shownAxisCodes}
						classificationSelection={filters.classification}
						onToggleClassification={filters.toggleClassification}
						onAddAxis={filters.addAxis}
						onRemoveAxis={filters.removeAxis}
					/>
				)}

				<div className="min-w-0 space-y-4">
					<div className="flex flex-wrap items-center gap-3">
						<Input
							aria-label="Search articles"
							value={filters.search}
							onChange={(event) => filters.setSearch(event.target.value)}
							// Says what it searches: the API deliberately does not match the
							// body, so promising otherwise here would be a lie the results tell.
							placeholder="Search title or summary…"
							className="max-w-xs"
						/>
						<Select
							aria-label="Sort articles"
							selectedKey={filters.sort}
							onSelectionChange={(key) => filters.setSort(String(key) as ArticleSort)}
						>
							{ARTICLE_SORTS.map((option) => (
								<ListBoxItem key={option.value} id={option.value}>
									{option.label}
								</ListBoxItem>
							))}
						</Select>
					</div>

					{isLoading ? (
						<ArticleResultListSkeleton />
					) : isError ? (
						<ArticleErrorState
							message={apiErrorMessage(error, 'The articles could not be read.')}
							onRetry={() => void refetch()}
						/>
					) : articles.length === 0 ? (
						filters.hasActiveFilters ? (
							<FilteredEmptyState activeFilters={filters.activeFilters} onClear={filters.clearAll} />
						) : (
							<EmptyCorpusState onCreate={() => void navigate('/articles/new')} />
						)
					) : (
						<ArticleResultList articles={articles} onOpen={openComposer} />
					)}
				</div>
			</div>
		</div>
	)
}
